import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Combobox,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  Toolbar,
  type ComboboxOption,
} from '@biovet/ui';
import { ApiError } from '../lib/api.js';
import type { PrintColumn, PrintDocument } from '../lib/print.js';
import { defaultUom } from '../lib/reference-data.js';
import { Fact, Th } from './parts.js';


export function useAction(reload: () => void) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const run = async <T extends { message: string }>(work: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setNotice(null);
    setProblem(null);
    try {
      const result = await work();
      setNotice(result.message);
      reload();
      return result;
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.full : String(cause));
      return null;
    } finally {
      setBusy(false);
    }
  };

  return { busy, notice, problem, run, clear: () => (setNotice(null), setProblem(null)) };
}

export function useSubmitShortcut(enabled: boolean, submit: () => void) {
  const latest = useRef(submit);
  latest.current = submit;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      latest.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);
}

export function SubmitHint() {
  return (
    <span className="text-[11px] whitespace-nowrap text-[var(--ink-3)]" data-noprint="1">
      <kbd className="bb-mono">Ctrl</kbd> + <kbd className="bb-mono">Enter</kbd> to post
    </span>
  );
}

export function Messages({ notice, problem }: { notice: string | null; problem: string | null }) {
  if (!notice && !problem) return null;
  return (
    <div className="flex flex-col gap-2.5 pb-3.5" data-noprint="1">
      {problem && (
        <Callout tone="danger" title="That was refused">
          {problem}
        </Callout>
      )}
      {notice && (
        <Callout tone="success" title="Done">
          {notice}
        </Callout>
      )}
    </div>
  );
}

export function Tiles({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3.5 pb-3.5 lg:grid-cols-4">{children}</div>;
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function EntryPanel({
  title,
  subtitle,
  open,
  onToggle,
  openLabel,
  children,
  actions,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  openLabel: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const close = useRef(onToggle);
  close.current = onToggle;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      const expanded = (event.target as HTMLElement | null)?.getAttribute('aria-expanded');
      if (event.key !== 'Escape' || expanded === 'true') return;
      close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;
  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {actions}
            <span className="text-[11px] whitespace-nowrap text-[var(--ink-3)]">
              <kbd className="bb-mono">Esc</kbd> to close
            </span>
          </>
        }
      />
      <CardBody className="flex flex-col gap-3.5">{children}</CardBody>
    </Card>
  );
}

export function PanelToggle({
  open,
  onToggle,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <Button size="sm" variant={open ? 'secondary' : 'primary'} onClick={onToggle}>
      {open ? 'Close' : label}
    </Button>
  );
}

export function FieldRow({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>;
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  hint,
  required,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date' | 'number';
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <Field label={label} hint={hint} required={required}>
      <Input
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function ReadOnlyField({
  label,
  value,
  hint,
  placeholder,
}: {
  label: string;
  value: string | null;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <div className="bb-row-h flex items-center">
        {value ? (
          <span className="bb-mono text-[13px] text-[var(--ink)]">{value}</span>
        ) : (
          <span className="text-[12.5px] text-[var(--ink-3)] italic">{placeholder ?? '—'}</span>
        )}
      </div>
    </Field>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
  required,
  placeholder = '—',
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <Field label={label} hint={hint} required={required}>
      <Select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

export function ComboField({
  label,
  value,
  onChange,
  options,
  hint,
  required,
  placeholder = 'Type to find…',
  disabled,
  caption,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  hint?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  caption?: string;
}) {
  return (
    <Field label={label} hint={hint} required={required}>
      <Combobox
        variant="field"
        value={value}
        options={options}
        placeholder={placeholder}
        caption={caption}
        disabled={disabled}
        aria-label={label}
        onChange={onChange}
      />
    </Field>
  );
}

export interface ProductUnits {
  sku: string;
  baseUom?: string;
  stockUom?: string | null;
  purchaseUom?: string | null;
  saleUom?: string | null;
}

export type LineRole = 'PURCHASE' | 'SALE' | 'STOCK';

export function pickedLine(
  sku: string,
  products: ProductUnits[] | undefined,
  role: LineRole,
  currentQty: string,
): { qty: string; uom: string } {
  const product = (products ?? []).find((p) => p.sku === sku);
  const own =
    role === 'PURCHASE'
      ? (product?.purchaseUom ?? product?.stockUom)
      : role === 'SALE'
        ? (product?.saleUom ?? product?.stockUom)
        : product?.stockUom;

  const preferred = own ?? product?.baseUom ?? null;

  return {
    qty: currentQty.trim() ? currentQty : '1',
    uom: product?.baseUom ? (preferred ?? product.baseUom) : defaultUom(preferred),
  };
}

export function useAutoRows<T>(
  rows: T[],
  setRows: (rows: T[]) => void,
  blank: () => T,
  isBlank: (row: T) => boolean,
) {
  const make = useRef(blank);
  make.current = blank;
  const test = useRef(isBlank);
  test.current = isBlank;

  useEffect(() => {
    const last = rows[rows.length - 1];
    if (rows.length === 0 || (last !== undefined && !test.current(last))) {
      setRows([...rows, make.current()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
}

export function useDefaultUnits<T extends { uomCode: string }>(
  rows: T[],
  setRows: (rows: T[]) => void,
) {
  const unit = defaultUom();
  const apply = useRef(setRows);
  apply.current = setRows;

  useEffect(() => {
    if (!unit || !rows.some((row) => row.uomCode === '')) return;
    apply.current(rows.map((row) => (row.uomCode === '' ? { ...row, uomCode: unit } : row)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, unit]);
}

export function dash(value: string | null | undefined): string {
  return value === null || value === undefined || value === '' ? '—' : value;
}

export function MasterTable({
  title,
  subtitle,
  loading,
  error,
  empty,
  columns,
  onNew,
  newLabel,
  toggle,
  children,
}: {
  title: string;
  subtitle: string;
  loading: boolean;
  error: string | undefined;
  empty: string;
  columns: string[];
  onNew?: () => void;
  newLabel: string;
  toggle: { on: boolean; onToggle: () => void; label: string };
  children: ReactNode;
}) {
  const rows = Array.isArray(children) ? children : [children];
  const hasRows = rows.flat().filter(Boolean).length > 0;

  return (
    <Card flush data-rpt="1">
      <CardHeader
        title={title}
        subtitle={subtitle}
        actions={
          <Toolbar>
            <Button size="sm" variant={toggle.on ? 'secondary' : 'ghost'} onClick={toggle.onToggle}>
              {toggle.label}
            </Button>
            <Button size="sm" variant="primary" disabled={!onNew} onClick={onNew}>
              {newLabel}
            </Button>
          </Toolbar>
        }
      />
      {loading ? (
        <SkeletonRows rows={6} />
      ) : error ? (
        <CardBody>
          <Callout tone="danger" title="That did not load">
            {error}
          </Callout>
        </CardBody>
      ) : !hasRows ? (
        <EmptyState title={empty} description="Nothing to show yet." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse">
            <thead>
              <tr className="bg-[var(--surface-2)]">
                {columns.map((c, i) => (
                  <Th key={c || i} align={i >= 3 && i < columns.length - 1 ? 'right' : 'left'}>
                    {c}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


export function DocumentPanel({
  doc,
  internal,
  loading = false,
  error,
  actions,
  onClose,
}: {
  doc: PrintDocument | null;
  internal?: { columns: PrintColumn[]; rows: string[][] };
  loading?: boolean;
  error?: string;
  actions?: ReactNode;
  onClose?: () => void;
}) {
  if (error) {
    return (
      <Callout tone="danger" title="This document could not be read">
        {error}
      </Callout>
    );
  }
  if (loading || !doc) {
    return (
      <Card flush className="mb-3.5" data-noprint="1">
        <CardBody>
          <SkeletonRows rows={5} cols={5} />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        title={`${doc.documentNo} · ${doc.title}`}
        subtitle={doc.watermark ?? undefined}
        actions={
          <>
            {actions}
            {onClose && (
              <Button size="sm" variant="ghost" onClick={onClose}>
                Close
              </Button>
            )}
          </>
        }
      />
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          {doc.facts
            .filter((f) => f.value)
            .map((f) => (
              <Fact key={f.label} label={f.label} value={f.value} />
            ))}
        </div>
      </CardBody>

      {(() => {
        const columns = [...doc.columns, ...(internal?.columns ?? [])];
        const rows = doc.rows.map((row, i) => [...row, ...(internal?.rows[i] ?? [])]);
        return (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  {columns.map((c, i) => (
                    <Th
                      key={`${c.label}-${i}`}
                      align={c.align === 'right' ? 'right' : 'left'}
                      width={c.width}
                    >
                      {c.label}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr className="bb-row-h border-b border-[var(--line-2)]">
                    <td
                      colSpan={columns.length}
                      className="px-[var(--pad)] text-[12.5px] text-[var(--ink-3)]"
                    >
                      This document has no lines.
                    </td>
                  </tr>
                )}
                {rows.map((row, i) => (
                  <tr key={i} className="bb-row-h border-b border-[var(--line-2)]">
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        className={
                          columns[j]?.align === 'right'
                            ? 'bb-num px-2.5 text-right text-[12.5px] tabular-nums'
                            : 'px-2.5 text-[12.5px]'
                        }
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {(doc.totals?.length || doc.narration || doc.notes?.length) && (
        <CardBody className="grid gap-3 md:grid-cols-[1fr_320px]">
          <div className="flex flex-col gap-2">
            {doc.narration && (
              <p className="m-0 text-[12.5px]">
                <span className="text-[var(--ink-3)]">Narration · </span>
                {doc.narration}
              </p>
            )}
            {doc.notes?.map((note) => (
              <p key={note} className="m-0 text-[12px] text-[var(--ink-3)]">
                {note}
              </p>
            ))}
            {internal && internal.columns.length > 0 && (
              <p className="m-0 text-[12px] text-[var(--ink-3)]">
                {internal.columns.map((c) => c.label).join(', ')}{' '}
                {internal.columns.length === 1 ? 'is' : 'are'} ours — not on the printed copy.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            {doc.totals?.map((t) => (
              <div
                key={t.label}
                className={
                  t.strong
                    ? 'flex justify-between border-t border-[var(--line-2)] pt-2 text-[13px] font-semibold'
                    : 'flex justify-between text-[12.5px]'
                }
              >
                <span className={t.strong ? undefined : 'text-[var(--ink-2)]'}>{t.label}</span>
                <span className="bb-num shrink-0 pl-3 tabular-nums">{t.value}</span>
              </div>
            ))}
          </div>
        </CardBody>
      )}
    </Card>
  );
}
